import { LissThresholds } from '@protector/utils/constants';
import { getNumberOrDefault, validateNumberOrZero } from '@protector/utils/number';
import { getNumericValueFromLocalStorage } from '@protector/utils/storage';
import type { Dictionary } from '@protector/utils/types';
import chroma from 'chroma-js';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import type { DevelopmentPhases } from '../crop/models.ts';
import type { CurrentSeasonArea, PropertyInfoAddress } from '../property/models.ts';
import { RegionType } from '../region/constants.ts';
import type { CurrentInfo, Region } from '../region/models.ts';
import { getDAEByEmergenceDay, getFieldsRecursive } from '../region/utils.ts';
import { normalizeSeverityLevel } from '../timeline/utils.ts';
import { BASE_PHENOLOGY_COLORS, BASE_SEEDS_COLORS, MAP_SEVERITY_COLORS, SEEDS_COLORS } from './constants.ts';
import type { ChoroplethLimits, StorageMaxDaysLimit } from './models.ts';

export const getChoroplethSeverityColor = (area: Region, crop_ended: boolean): string => {
  let backgroundColor = 'transparent';
  const colors = MAP_SEVERITY_COLORS;
  if (!area.current_info) {
    return backgroundColor;
  }
  if (area.current_info && crop_ended) {
    backgroundColor = '#C1C5C8';
  } else if (area.current_info.last_monitoring && validateNumberOrZero(area.current_info.severity_level)) {
    const severityLevel = normalizeSeverityLevel(area.current_info.severity_level);
    if (severityLevel < (LissThresholds.MIN_CONTROL as number)) {
      backgroundColor = colors[0];
    } else if (severityLevel < (LissThresholds.MIN_DAMAGE as number)) {
      backgroundColor = colors[1];
    } else {
      backgroundColor = colors[2];
    }
  }
  return backgroundColor;
};

export const getStorageDaysLimit = (): StorageMaxDaysLimit => {
  return {
    daysWithoutMonitoringMax: getNumericValueFromLocalStorage(`days_without_monitoring_limit_days`),
    daysWithoutMonitoringMaxColor: localStorage.getItem(`days_without_monitoring_limit_days_color`),
    daysWithoutSprayMax: getNumericValueFromLocalStorage(`days_without_spray_limit_days`),
    daysWithoutSprayMaxColor: localStorage.getItem(`days_without_spray_limit_days_color`),
    timesAppliedMax: getNumericValueFromLocalStorage(`times_applied_limit_days`),
    timesAppliedMaxColor: localStorage.getItem(`times_applied_limit_days_color`),
    daysAfterEmergenceMax: getNumericValueFromLocalStorage(`days_after_emergence_limit_days`),
    daysAfterEmergenceMaxColor: localStorage.getItem(`days_after_emergence_limit_days_color`),
    daysAfterEmergenceMin: getNumericValueFromLocalStorage(`days_after_emergence_min_limit_days`),
    daysAfterEmergenceMinColor: localStorage.getItem(`days_after_emergence_min_limit_days_color`)
  };
};

export const extractChildrenAreasFromRegions = (regions: Dictionary<Region> | Region[]): Region[] => {
  if (!Array.isArray(regions)) {
    return Object.values(regions);
  }
  const hasChildren = regions.filter((region: Region) => region.children?.length);
  if (!hasChildren.length) {
    return regions;
  }
  return regions.flatMap(region => getFieldsRecursive(region));
};

export const getLimitsChoropleth = (
  regions: Dictionary<Region> | Region[],
  propertyData: PropertyInfoAddress | null,
  currentAreaSeasons: CurrentSeasonArea[],
  selectedSeasonsIds: string[],
  storageMaxDaysLimit?: StorageMaxDaysLimit
): Dictionary<ChoroplethLimits> => {
  let maxDaysWithoutSpray = -Infinity;
  let maxDaysWithoutMonitoring = -Infinity;
  let maxDaysAfterEmergence = -Infinity;

  const timezone = propertyData?.timeZone;

  const getDate = (data: moment.Moment) => {
    return timezone ? momentTimezone.tz(data, timezone) : data;
  };

  const regionsArray = extractChildrenAreasFromRegions(regions);

  const currentStartOfTheDay = getDate(moment()).startOf('day');

  const currentAreaSeasonsIdsSet = new Set(
    currentAreaSeasons.filter(seasonArea => selectedSeasonsIds.includes(seasonArea.seasonId)).map(seasonArea => seasonArea.areaId)
  );
  regionsArray
    .filter(r => r.type === RegionType.AREA && r.current_info && currentAreaSeasonsIdsSet.has(r.id))
    .forEach(region => {
      if (!region.current_info) return;
      const { last_spray: lastSpray, last_monitoring: lastMonitoring, emergence_day: emergenceDay } = region.current_info;
      const dae = getDAEByEmergenceDay(emergenceDay);

      if (lastSpray) {
        const lastSprayDate = getDate(moment(lastSpray.end_date || lastSpray.start_date)).startOf('day');
        const daysWithoutSpray = currentStartOfTheDay.diff(lastSprayDate, 'days');
        maxDaysWithoutSpray = Math.max(daysWithoutSpray, maxDaysWithoutSpray);
      }

      if (lastMonitoring) {
        const lastMonitoringDate = getDate(moment(lastMonitoring.date)).startOf('day');
        const daysWithoutMonitoring = currentStartOfTheDay.diff(lastMonitoringDate, 'days');
        maxDaysWithoutMonitoring = Math.max(daysWithoutMonitoring, maxDaysWithoutMonitoring);
      }

      if (dae) {
        maxDaysAfterEmergence = Math.max(dae, maxDaysAfterEmergence);
      }
    });

  const daysWithoutSprayMax = getNumberOrDefault(storageMaxDaysLimit?.daysWithoutSprayMax, Math.max(maxDaysWithoutSpray, 10));
  const daysWithoutMonitoringMax = getNumberOrDefault(
    storageMaxDaysLimit?.daysWithoutMonitoringMax,
    Math.max(maxDaysWithoutMonitoring, 10)
  );
  const daysAfterEmergenceMax = getNumberOrDefault(storageMaxDaysLimit?.daysAfterEmergenceMax, Math.max(maxDaysAfterEmergence, 10));

  return {
    days_without_spray: {
      max: daysWithoutSprayMax,
      min: 0
    },
    days_without_monitoring: {
      max: daysWithoutMonitoringMax,
      min: 0
    },
    days_after_emergence: {
      max: daysAfterEmergenceMax,
      min: storageMaxDaysLimit?.daysAfterEmergenceMin ?? 0
    }
  };
};

type ColorDictionary = Record<string, { color: string; area: number; count: number; cropId?: string }>;

const insertColorInColorDictionaryFromColorsArray = (
  colorDictionary: ColorDictionary,
  colorKey: string | undefined,
  cropId: string | undefined,
  otherInfos: Record<'area', number>,
  colorsList: string[]
) => {
  if (colorKey && !colorDictionary[colorKey]) {
    const color = colorsList.pop() ?? '';

    colorDictionary[colorKey] = { color, count: 1, area: otherInfos.area, cropId };

    return colorDictionary;
  }
  if (colorKey && colorDictionary[colorKey]) {
    colorDictionary[colorKey] = {
      ...colorDictionary[colorKey],
      count: colorDictionary[colorKey].count + 1,
      area: otherInfos.area + colorDictionary[colorKey].area,
      cropId
    };

    return colorDictionary;
  }

  return colorDictionary;
};

export const getSortedSeedsChoropleth = (currentInfoRegions: Record<string, CurrentInfo>): ColorDictionary => {
  if (!currentInfoRegions) {
    return {};
  }
  const uniqueSeedCrops = Object.values(currentInfoRegions).reduce((seedCrops, currentInfo) => {
    if (currentInfo.crop_ended) {
      return seedCrops;
    }
    const seed = currentInfo.seeds?.[0];
    const cropId = currentInfo.crops?.[0].id;
    if (!seed || !cropId) {
      return seedCrops;
    }
    return {
      ...seedCrops,
      [seed]: cropId
    };
  }, {});
  const mapSeedColors: string[] = chroma.scale(BASE_SEEDS_COLORS).mode('lch').colors(Object.keys(uniqueSeedCrops).length);
  mapSeedColors.reverse();
  return Object.values(currentInfoRegions)
    .sort((currentInfoA, currentInfoB) => {
      const seedA: string = currentInfoA.seeds?.[0] ?? '';
      const seedB: string = currentInfoB.seeds?.[0] ?? '';
      return seedA.localeCompare(seedB);
    })
    .reduce((seedColors, currentInfo) => {
      if (currentInfo.crop_ended) {
        return seedColors;
      }
      const seed = currentInfo.seeds?.[0];
      const cropId = currentInfo.crops?.[0].id;
      return insertColorInColorDictionaryFromColorsArray(seedColors, seed, cropId, { area: currentInfo.area_in_hectares }, mapSeedColors);
    }, {});
};

const sortPhenologyByDAE = (
  currentInfoA: CurrentInfo,
  currentInfoB: CurrentInfo,
  phenologyStagesByCropAndName: Record<string, Record<string, DevelopmentPhases>>
) => {
  const cropA = currentInfoA.crops?.[0].id ?? '';
  const cropB = currentInfoB.crops?.[0].id ?? '';
  const phenologicalStageKeyA = currentInfoA.phenological_stage ?? '';
  const phenologicalStageKeyB = currentInfoB.phenological_stage ?? '';

  const phenologicalStageA = phenologyStagesByCropAndName[cropA]?.[phenologicalStageKeyA];
  const phenologicalStageB = phenologyStagesByCropAndName[cropB]?.[phenologicalStageKeyB];
  return (phenologicalStageB?.days_after_emergency || 0) - (phenologicalStageA?.days_after_emergency || 0);
};

export const getSortedPhenologicalStageColor = (
  currentInfoRegions: Record<string, CurrentInfo>,
  phenologyStagesByCropAndName: Record<string, Record<string, DevelopmentPhases>>
): ColorDictionary => {
  if (!currentInfoRegions) {
    return {};
  }
  const uniquePhenologyColors = Object.values(currentInfoRegions).reduce((phenologyCrops, currentInfo) => {
    const phenologicalStage = currentInfo.phenological_stage;
    const cropId = currentInfo.crops?.[0].id;

    if (currentInfo.crop_ended || !phenologicalStage || !cropId) {
      return phenologyCrops;
    }
    return {
      ...phenologyCrops,
      [phenologicalStage]: cropId
    };
  }, {});

  const mapPhenologyColors: string[] = chroma.scale(BASE_PHENOLOGY_COLORS).mode('lch').colors(Object.keys(uniquePhenologyColors).length);

  return Object.values(currentInfoRegions)
    .sort((currentInfoA, currentInfoB) => sortPhenologyByDAE(currentInfoA, currentInfoB, phenologyStagesByCropAndName))
    .reduce((phenologicalStagesColor, currentInfo) => {
      const phenologicalStage = currentInfo.phenological_stage;
      const cropId = currentInfo.crops?.[0].id;
      if (currentInfo.crop_ended) {
        return phenologicalStagesColor;
      }
      return insertColorInColorDictionaryFromColorsArray(
        phenologicalStagesColor,
        phenologicalStage,
        cropId,
        { area: currentInfo.area_in_hectares },
        mapPhenologyColors
      );
    }, {});
};

const SEEDS_COLORS_ITEMS: string[] = [];

const seedToHexColor = (seed: string) => {
  const numberSeedHex = `#${(
    101010 *
    seed
      .toLowerCase()
      .split('')
      .map(s => s.charCodeAt(0))
      .reduce((x, y) => x + y, 0) *
    2
  )
    .toString(16)
    .slice(0, 6)
    .toUpperCase()}`;

  const numberSeed = numberSeedHex
    .toLowerCase()
    .split('')
    .map(s => s.charCodeAt(0))
    .reduce((x, y) => x + y, 0);

  const seedColorIndex = numberSeed % 21;
  if (!SEEDS_COLORS_ITEMS.includes(SEEDS_COLORS[seedColorIndex])) {
    SEEDS_COLORS_ITEMS.push(SEEDS_COLORS[seedColorIndex]);
  } else {
    for (let i = 0; i <= SEEDS_COLORS.length; i++) {
      if (!SEEDS_COLORS_ITEMS.includes(SEEDS_COLORS[i])) {
        SEEDS_COLORS_ITEMS.push(SEEDS_COLORS[i]);
        break;
      }
    }
  }

  return SEEDS_COLORS_ITEMS[SEEDS_COLORS_ITEMS.length - 1];
};

const insertColorInColorDictionary = (
  colorDictionary: ColorDictionary,
  colorKey: string | undefined,
  cropId: string | undefined,
  otherInfos: Record<'area', number>
) => {
  if (colorKey && !colorDictionary[colorKey]) {
    colorDictionary[colorKey] = { color: seedToHexColor(colorKey), count: 1, area: otherInfos.area, cropId };

    return colorDictionary;
  }

  if (colorKey && colorDictionary[colorKey]) {
    colorDictionary[colorKey] = {
      ...colorDictionary[colorKey],
      count: colorDictionary[colorKey].count + 1,
      area: otherInfos.area + colorDictionary[colorKey].area,
      cropId
    };

    return colorDictionary;
  }

  return colorDictionary;
};

export const getSeedsChoropleth = (currentInfoRegions: Record<string, CurrentInfo>): ColorDictionary => {
  SEEDS_COLORS_ITEMS.splice(0);
  return Object.values(currentInfoRegions).reduce((seedColors, currentInfo) => {
    if (currentInfo.crop_ended) {
      return seedColors;
    }
    const seed = currentInfo.seeds?.[0];
    const cropId = currentInfo.crops?.[0].id;
    return insertColorInColorDictionary(seedColors, seed, cropId, { area: currentInfo.area_in_hectares });
  }, {});
};

export const getPhenologicalStageColor = (currentInfoRegions: Record<string, CurrentInfo>): ColorDictionary => {
  SEEDS_COLORS_ITEMS.splice(0);
  return Object.values(currentInfoRegions).reduce((phenologicalStagesColor, currentInfo) => {
    const phenologicalStage = currentInfo.phenological_stage;
    const cropId = currentInfo.crops?.[0].id;
    if (currentInfo.crop_ended) {
      return phenologicalStagesColor;
    }
    return insertColorInColorDictionary(phenologicalStagesColor, phenologicalStage, cropId, { area: currentInfo.area_in_hectares });
  }, {});
};
